/**
 * StudySync Study Dashboard Component
 * Detailed view for managing individual studies, participants, and data
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChatTab } from './chat/ChatTab';
import { ChatUnreadBadge } from './chat/ChatUnreadBadge';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Skeleton,
  Popover,
  List,
  ListItem,
  ListItemText,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  People,
  Email,
  Apple,
  Delete,
  Refresh,
  ContentCopy,
  ArrowBack,
  Edit,
  Visibility,
  CheckCircle,
  Warning,
  Send,
  Launch,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSecureAuth } from "../../contexts/SecureAuthContext";
import { apiService, GOOGLE_HEALTH_UI_CONFIG } from "../../config/api";
import { StudyService } from "../../services/studyService";
import { EmailService } from "../../services/emailService";
import { ParticipantService } from "../../services/participantService";
import { FitbitService } from "../../services/fitbitService";
import { issueFitbitCollectionParticipantInvitation } from "../../services/fitbitCollectionEnrollmentService";
import { GoogleHealthService } from "../../services/googleHealthService";
import { ApiError } from "../../utils/api";
import { existingParticipantReason, invitationOutcome } from "./fitbitInvitationOutcome";
import GoogleHealthScopeUpgradeButton, {
  isActiveSyntheticGoogleHealthParticipant,
} from "./GoogleHealthScopeUpgradeButton";
import DataExtractionInterface from "./DataExtractionInterface";
import FitbitCollectionDataView from "./FitbitCollectionDataView";
import ParticipantHistoryProgress from "./fitbit/ParticipantHistoryProgress";
import StudyDataExport from "./StudyDataExport";
import { CollectionSourceProvider } from '../../contexts/CollectionSourceContext';
import { CollectionSourceId, ultrahumanEnabled } from '../../config/collectionSources';
import CollectionSourcePicker from './collection/CollectionSourcePicker';
import UltrahumanInviteDialog from './collection/UltrahumanInviteDialog';
import HealthKitQRGenerator from "./HealthKitQRGenerator";
import NotificationPopup from "../common/NotificationPopup";
import OutdatedStudyNotice from "../common/OutdatedStudyNotice";
import { useNotifications } from "../../hooks/useNotifications";
import type {
  Study,
  Participant,
  EmailHistoryRecord,
  PendingParticipant,
  StudyMember,
} from "../../types";
import { formatPhoneNumber } from "../../utils/textUtils";
import {
  formatDeviceSources,
  historySources,
  invitedSources,
  sourceLabel,
} from "../../utils/deviceType";
import { participantStatus } from "../../utils/participantStatus";
import AlertsTab from "./AlertsTab";
import TrackingIdTab from "./TrackingIdTab";
import ResearcherTabLayout from "./ResearcherTabLayout";
import { researcherTableSx, researcherTableContainerSx, researcherTableHeaderSx, researcherNameSx, researcherMetaSx } from "./researcherPresentation";
import PendingParticipantsTable from "./PendingParticipantsTable";
import { ParticipantDeletionDialog } from "./study/StudyDeletionDialog";
import DeletionConfirmation from "./study/DeletionConfirmation";
import EmailHistoryTab from "./EmailHistoryTab";
import StudyMetricsSummary from "./StudyMetricsSummary";
import StudyMembersTab from "./study/StudyMembersTab";
import StudyNavigation from "./study/StudyNavigation";
import StudySectionPanels from "./study/StudySectionPanels";
import { studySections, validStudyTabs, sectionForTab, studyNavigationItems, StudySectionKey, StudySection, ParticipantView } from "./study/studySections";
import StudyEditDialog from "./study/StudyEditDialog";
import StudyEmailDetailsDialog from "./study/StudyEmailDetailsDialog";
import ParticipantDetailsDialog, { ParticipantDetails } from "./study/ParticipantDetailsDialog";
import {
  InviteParticipant,
  ParticipantInviteForm,
  ParticipantInviteList,
  ParticipantContactForm,
  ParticipantInviteFormState,
  ParticipantContactFormState,
} from "./study/ParticipantEmailForms";
import { analyticsService } from "../../services/analyticsService";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  loading?: boolean;
  title?: string;
  participantView?: ParticipantView;
}

const CHAT_TAB_INDEX = studySections.chat.tab;

type ParticipantDeletionJob = {
  participantId: string;
  status?: string;
  currentStep?: string;
  progressMessage?: string;
  progressDetails?: any;
  heartbeatAt?: string;
  updatedAt?: string;
};

const TabSkeleton: React.FC = () => (
  <Box sx={{ py: 1, px: 2 }}>
    {[...Array(5)].map((_, i) => (
      <Skeleton
        key={i}
        variant="rectangular"
        height={52}
        sx={{ mb: 1, borderRadius: 0.5 }}
      />
    ))}
  </Box>
);

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  loading,
  title,
  participantView,
}) => {
  const section = sectionForTab(index, participantView);
  const Icon = section?.Icon || People;
  return (
    <div hidden={value !== index}>
      {value === index && (
        index === studySections.extraction.tab ? <Box sx={{ py: 0.5 }}>{loading ? <TabSkeleton /> : children}</Box> :
          <ResearcherTabLayout title={title || section?.title || section?.label || 'Study'} icon={<Icon />}>
            {loading ? <TabSkeleton /> : children}
          </ResearcherTabLayout>
      )}
    </div>
  );
};

const DAY_MS = 24 * 60 * 60 * 1000;
const HISTORICAL_EXTRACTION_DAYS_PER_HOUR = 23;

type HistoricalProgressInfo =
  | {
      kind: "determinate";
      percentage: number;
      label: string;
      tooltip: React.ReactNode;
    }
  | {
      kind: "failed";
      percentage: number;
      label: string;
      tooltip: React.ReactNode;
    };

const parseUtcDate = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;
  const normalized = String(dateStr).split("T")[0];
  const parsed = new Date(`${normalized}T12:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const diffUtcDays = (later: Date, earlier: Date): number =>
  Math.max(0, Math.floor((later.getTime() - earlier.getTime()) / DAY_MS));

const formatEstimatedCompletionTime = (remainingDays: number): string => {
  if (remainingDays <= 0) {
    return "Less than 1 hour";
  }

  const estimatedHours = Math.ceil(
    remainingDays / HISTORICAL_EXTRACTION_DAYS_PER_HOUR,
  );

  if (estimatedHours <= 1) {
    return "About 1 hour";
  }

  if (estimatedHours < 24) {
    return `About ${estimatedHours} hours`;
  }

  const estimatedDays = estimatedHours / 24;

  if (estimatedDays < 2) {
    return `About ${estimatedHours} hours (~1 day)`;
  }

  return `About ${estimatedHours} hours (~${Math.ceil(estimatedDays)} days)`;
};

const buildHistoricalProgressTooltip = (
  title: string,
  details?: React.ReactNode,
) => (
  <Box sx={{ color: "inherit" }}>
    <Typography variant="body2" color="inherit" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    {details}
  </Box>
);

const getHistoricalProgressInfo = (
  participant: Participant,
): HistoricalProgressInfo | null => {
  const historicalExtraction = participant.historicalExtraction;
  if (!historicalExtraction) {
    return null;
  }

  const status = historicalExtraction.status;
  const todayDateStr = new Date().toISOString().split("T")[0];

  if (status === "completed" && !historicalExtraction.memberSince) {
    return {
      kind: "determinate",
      percentage: 100,
      label: "Completed",
      tooltip: buildHistoricalProgressTooltip(
        "Historical extraction completed",
      ),
    };
  }

  if (!historicalExtraction.memberSince) {
    return {
      kind: status === "failed" ? "failed" : "determinate",
      percentage: status === "completed" ? 100 : 0,
      label:
        status === "completed"
          ? "Completed"
          : status === "failed"
            ? "Needs retry"
            : "In progress",
      tooltip:
        status === "completed"
          ? buildHistoricalProgressTooltip("Historical extraction completed")
          : status === "failed"
            ? buildHistoricalProgressTooltip(
                "Historical extraction needs retry",
              )
            : buildHistoricalProgressTooltip(
                "Historical extraction in progress",
              ),
    };
  }

  const today = parseUtcDate(todayDateStr);
  const memberSince = parseUtcDate(historicalExtraction.memberSince);
  if (!today || !memberSince) {
    return {
      kind: status === "failed" ? "failed" : "determinate",
      percentage: status === "completed" ? 100 : 0,
      label:
        status === "completed"
          ? "Completed"
          : status === "failed"
            ? "Needs retry"
            : "In progress",
      tooltip:
        status === "completed"
          ? buildHistoricalProgressTooltip("Historical extraction completed")
          : status === "failed"
            ? buildHistoricalProgressTooltip(
                "Historical extraction needs retry",
              )
            : buildHistoricalProgressTooltip(
                "Historical extraction in progress",
              ),
    };
  }

  const totalDays = Math.max(1, diffUtcDays(today, memberSince));
  const cursorDateStr =
    status === "completed"
      ? historicalExtraction.earliestDateFound ||
        historicalExtraction.memberSince
      : historicalExtraction.lastExtractedDate || todayDateStr;
  const cursorDate = parseUtcDate(cursorDateStr) || today;
  const processedDays =
    status === "completed"
      ? totalDays
      : Math.min(totalDays, diffUtcDays(today, cursorDate));
  const remainingDays = Math.max(0, totalDays - processedDays);
  const percentage =
    status === "completed"
      ? 100
      : Math.min(
          100,
          Math.max(0, Math.round((processedDays / totalDays) * 100)),
        );

  if (status === "failed") {
    return {
      kind: "failed",
      percentage,
      label: "Needs retry",
      tooltip:
        percentage > 0
          ? buildHistoricalProgressTooltip(
              `Historical extraction paused at ${percentage}%`,
            )
          : buildHistoricalProgressTooltip("Historical extraction needs retry"),
    };
  }

  return {
    kind: "determinate",
    percentage,
    label: status === "completed" ? "Completed" : "In progress",
    tooltip:
      status === "completed"
        ? buildHistoricalProgressTooltip("Historical extraction completed")
        : buildHistoricalProgressTooltip(
            `Historical extraction: ${percentage}%`,
            <>
              <Typography
                variant="caption"
                color="inherit"
                sx={{ display: "block", mt: 0.5 }}
              >
                Estimated time to completion:{" "}
                {formatEstimatedCompletionTime(remainingDays)}
              </Typography>
            </>,
          ),
  };
};

const StudyDashboard: React.FC = () => {
  const { studyId } = useParams<{ studyId: string }>();
  const navigate = useNavigate();
  const { user } = useSecureAuth();
  const { notification, showSuccess, showError, clearNotification } =
    useNotifications();

  const [study, setStudy] = useState<Study | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [collectionSource, setCollectionSource] = useState<CollectionSourceId>('fitbit');
  const [ultrahumanInviteOpen, setUltrahumanInviteOpen] = useState(false);
  const [fitbitParticipantToDelete, setFitbitParticipantToDelete] = useState<Participant | null>(null);
  const [deletingParticipantIds, setDeletingParticipantIds] = useState<
    string[]
  >([]);
  const [deletionJobsByParticipantId, setDeletionJobsByParticipantId] =
    useState<Record<string, ParticipantDeletionJob>>({});
  const [pendingParticipants, setPendingParticipants] = useState<
    PendingParticipant[]
  >([]);
  const [emailHistory, setEmailHistory] = useState<EmailHistoryRecord[]>([]);
  const [emailHistoryError, setEmailHistoryError] = useState("");
  const [studyMembers, setStudyMembers] = useState<StudyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabValue = Number(searchParams.get('tab') || 0);
  const selectTabValue = useCallback((nextTabValue: number) => {
    setSearchParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', String(nextTabValue));
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  const [participantViewMode, setParticipantViewMode] = useState<
    "participant" | "pending"
  >("participant");
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [copiedStudyCode, setCopiedStudyCode] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);

  const alertsTabIndex = studySections.alerts.tab;
  const trackingIdTabIndex = studySections.tracking.tab;
  const activeTabGroup = sectionForTab(tabValue, participantViewMode)?.group || 'participants';

  // Dialog states
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [fitbitInviteMessage, setFitbitInviteMessage] = useState({
    subject: 'Invitation to join our study on StudySync',
    body: 'You are invited to participate in our study. Please use your personal link below to connect Fitbit and complete your participant profile.',
  });
  const [iosInviteDialogOpen, setIosInviteDialogOpen] = useState(false);
  const [iosInviteMode, setIosInviteMode] = useState<
    "qr" | "invite" | "contact"
  >("qr");
  const [contactCollaboratorDialogOpen, setContactCollaboratorDialogOpen] =
    useState(false);

  // Unified participant email mode
  const [participantEmailMode, setParticipantEmailMode] = useState<
    "invite" | "contact"
  >("invite");

  // Unified collaborator email mode
  const [collaboratorEmailMode, setCollaboratorEmailMode] = useState<
    "invite" | "contact"
  >("invite");
  const [iosInviteReviewOpen, setIosInviteReviewOpen] = useState(false);
  const [iosContactReviewOpen, setIosContactReviewOpen] = useState(false);
  const [contactReviewOpen, setContactReviewOpen] = useState(false);
  const [contactCollaboratorReviewOpen, setContactCollaboratorReviewOpen] =
    useState(false);
  const [inviteCollaboratorReviewOpen, setInviteCollaboratorReviewOpen] =
    useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState<string | null>(
    null,
  );
  const [participantDeleteConfirmation, setParticipantDeleteConfirmation] = useState("");
  const participantDeletionId = participants.find(participant => participant.id === participantToDelete)?.participantId || participantToDelete || "";
  const participantDeletionConfirmed = Boolean(participantDeletionId) && participantDeleteConfirmation === participantDeletionId;
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailHistoryRecord | null>(
    null,
  );
  const [editPendingDialogOpen, setEditPendingDialogOpen] = useState(false);
  const [editParticipantDialogOpen, setEditParticipantDialogOpen] =
    useState(false);
  const [deletePendingDialogOpen, setDeletePendingDialogOpen] = useState(false);
  const [pendingParticipantToDelete, setPendingParticipantToDelete] = useState<
    { participant: PendingParticipant; cancelOnly: boolean } | null
  >(null);
  const [pendingParticipantToEdit, setPendingParticipantToEdit] =
    useState<PendingParticipant | null>(null);
  const [participantToEdit, setParticipantToEdit] = useState<Participant | null>(
    null,
  );
  const [participantDetailsDialogOpen, setParticipantDetailsDialogOpen] =
    useState(false);
  const [selectedParticipantDetails, setSelectedParticipantDetails] =
    useState<ParticipantDetails | null>(null);

  // Loading states
  const [editLoading, setEditLoading] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);

  // Form states for invitation
  const [inviteForm, setInviteForm] = useState<ParticipantInviteFormState>({
    studyName: "",
    description: "",
    duration: "",
    participants: [{ email: "", phoneNumber: "" }],
  });

  const [iosInviteForm, setIosInviteForm] =
    useState<ParticipantInviteFormState>({
      studyName: "",
      description: "",
      duration: "",
      participants: [{ email: "", phoneNumber: "" }],
    });

  // Form states for contact participants
  const [contactForm, setContactForm] = useState<ParticipantContactFormState>({
    emailTitle: "",
    content: "",
    recipients: [],
  });

  const [iosContactForm, setIosContactForm] =
    useState<ParticipantContactFormState>({
      emailTitle: "",
      content: "",
      recipients: [],
    });

  // Form states for contact collaborator
  const [contactCollaboratorForm, setContactCollaboratorForm] = useState({
    emailTitle: "",
    content: "",
    recipients: [] as string[],
  });

  // Re-auth (Fitbit: participants missing SpO2/breathing scope)
  type ParticipantNeedingReauth = {
    participantId: string;
    participantEmail?: string;
    firstName?: string;
    lastName?: string;
    scope?: string;
    reauthorizationKind?: "fitbit_legacy_scope" | "google_health_migration";
    provider?: "fitbit_legacy" | "google_health";
  };
  const [participantsNeedingReauth, setParticipantsNeedingReauth] = useState<
    ParticipantNeedingReauth[]
  >([]);
  const [reauthAnchorEl, setReauthAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [reauthSendDialogOpen, setReauthSendDialogOpen] = useState(false);
  const [reauthSendParticipant, setReauthSendParticipant] =
    useState<ParticipantNeedingReauth | null>(null);
  const [reauthEmailSubject, setReauthEmailSubject] = useState("");
  const [reauthEmailBody, setReauthEmailBody] = useState("");
  const [reauthSending, setReauthSending] = useState(false);

  // Form states for invite collaborator
  const [inviteCollaboratorForm, setInviteCollaboratorForm] = useState({
    emailTitle: "",
    content: "",
    recipients: [] as string[],
  });

  // Review form states for editing in review mode
  const [iosInviteReviewForm, setIosInviteReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  // Review form states for invite collaborator
  const [inviteCollaboratorReviewForm, setInviteCollaboratorReviewForm] =
    useState({
      emailTitle: "",
      content: "",
    });

  const [contactReviewForm, setContactReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  const [iosContactReviewForm, setIosContactReviewForm] = useState({
    emailTitle: "",
    content: "",
  });

  const [contactCollaboratorReviewForm, setContactCollaboratorReviewForm] =
    useState({
      emailTitle: "",
      content: "",
    });

  // Form states for pending participants
  const [editPendingForm, setEditPendingForm] = useState({
    phoneNumber: "",
  });
  const [editParticipantForm, setEditParticipantForm] = useState({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    if (studyId) {
      loadStudyData();
    }
  }, [studyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyDeletionJobs = (jobs: ParticipantDeletionJob[]) => {
    setDeletingParticipantIds(jobs.map((job) => job.participantId));
    setDeletionJobsByParticipantId(
      jobs.reduce<Record<string, ParticipantDeletionJob>>(
        (accumulator, job) => {
          accumulator[job.participantId] = job;
          return accumulator;
        },
        {},
      ),
    );
  };

  // Removed or unknown tabs return to Participants.
  useEffect(() => {
    if (!validStudyTabs.has(tabValue)) selectTabValue(studySections.participants.tab);
  }, [tabValue, selectTabValue]);

  // Refetch participants needing re-auth when switching to Participants tab so list is fresh after onboarding
  useEffect(() => {
    if (tabValue !== studySections.participants.tab || !studyId) return;
    let cancelled = false;
    (async () => {
      const token = await apiService.getAuthToken();
      if (!token || cancelled) return;
      try {
        const reauthResponse: any =
          await FitbitService.getParticipantsNeedingReauth(studyId, token);
        if (
          !cancelled &&
          reauthResponse?.success &&
          Array.isArray(reauthResponse.participants)
        ) {
          setParticipantsNeedingReauth(reauthResponse.participants);
        } else if (!cancelled) {
          setParticipantsNeedingReauth([]);
        }
      } catch {
        if (!cancelled) setParticipantsNeedingReauth([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tabValue, studyId]);

  const loadStudyData = async ({ background = false }: { background?: boolean } = {}) => {
    if (!studyId) return;
    const requestId = ++loadRequestIdRef.current;

    try {
      if (!background) setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to load study data",
          "Load Study",
        );
        return;
      }

      const [
        studyResult,
        participantsResult,
        emailResult,
        pendingResult,
        membersResult,
      ] = await Promise.allSettled([
        StudyService.getStudy(studyId, token),
        StudyService.getStudyParticipants(studyId, token),
        EmailService.getEmailHistory(studyId, token),
        StudyService.getPendingParticipants(studyId, token),
        StudyService.getStudyMembers(studyId, token),
      ]);

      if (requestId !== loadRequestIdRef.current) {
        return;
      }

      if (studyResult.status === "fulfilled") {
        const studyResponse: any = studyResult.value;
        if (studyResponse.success && studyResponse.study) {
          setStudy(studyResponse.study);
        }
      }

      if (participantsResult.status === "fulfilled") {
        const participantsResponse: any = participantsResult.value;
        if (participantsResponse.success && participantsResponse.participants) {
          setParticipants(participantsResponse.participants);
        } else {
          setParticipants([]);
        }
      }

      setEmailHistoryError("");
      if (emailResult.status === "fulfilled") {
        const emailResponse: any = emailResult.value;
        if (emailResponse.success && emailResponse.history) {
          setEmailHistory(emailResponse.history);
        } else {
          setEmailHistory([]);
          setEmailHistoryError("Could not load email history. Please refresh.");
        }
      } else {
        setEmailHistory([]);
        setEmailHistoryError("Could not load email history. Please refresh.");
      }

      if (pendingResult.status === "fulfilled") {
        const pendingResponse: any = pendingResult.value;
        if (pendingResponse.success && pendingResponse.data) {
          setPendingParticipants(pendingResponse.data);
        } else {
          setPendingParticipants([]);
        }
      }

      if (membersResult.status === "fulfilled") {
        const membersResponse: any = membersResult.value;
        if (membersResponse.success && membersResponse.members) {
          setStudyMembers(membersResponse.members);
        } else {
          setStudyMembers([]);
        }
      }

      if (studyResult.status === "fulfilled") {
        const studyResponse: any = studyResult.value;
        if (studyResponse.success && studyResponse.study?.studyCode) {
          try {
            const deletionJobsResponse =
              await ParticipantService.getActiveDeletionJobs(
                studyResponse.study.studyCode,
                token,
              );
            if (
              deletionJobsResponse.success &&
              Array.isArray(deletionJobsResponse.jobs)
            ) {
              applyDeletionJobs(
                deletionJobsResponse.jobs as ParticipantDeletionJob[],
              );
            } else {
              applyDeletionJobs([]);
            }
          } catch {
            applyDeletionJobs([]);
          }
        }
      }

      const firstRejected = [
        studyResult,
        participantsResult,
        emailResult,
        pendingResult,
        membersResult,
      ].find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected",
      );

      if (firstRejected && studyResult.status !== "fulfilled") {
        throw firstRejected.reason;
      }
    } catch (err: any) {
      if (requestId !== loadRequestIdRef.current) {
        return;
      }
      showError(
        "Failed to Load Study",
        err.message || "An unexpected error occurred while loading study data",
        "Load Study",
      );
    } finally {
      if (requestId === loadRequestIdRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!study?.studyCode || deletingParticipantIds.length === 0) {
      return;
    }

    let cancelled = false;
    let pollCount = 0;
    let timeoutId: number | null = null;

    const scheduleNextPoll = () => {
      const nextDelay = pollCount < 3 ? 5000 : 15000;
      timeoutId = window.setTimeout(runPoll, nextDelay);
    };

    const runPoll = async () => {
      const token = await apiService.getAuthToken();
      if (!token || cancelled) {
        return;
      }

      try {
        const deletionJobsResponse =
          await ParticipantService.getActiveDeletionJobs(
            study.studyCode,
            token,
          );
        if (cancelled) {
          return;
        }
        const nextDeletingJobs =
          deletionJobsResponse.success &&
          Array.isArray((deletionJobsResponse as any).jobs)
            ? ((deletionJobsResponse as any).jobs as ParticipantDeletionJob[])
            : [];
        const hadDeleting = deletingParticipantIds.length > 0;
        applyDeletionJobs(nextDeletingJobs);
        if (hadDeleting && nextDeletingJobs.length === 0) {
          await loadStudyData();
          return;
        }
        pollCount += 1;
      } catch {
        // Keep current UI state; next poll can recover.
        pollCount += 1;
      }
      if (!cancelled) {
        scheduleNextPoll();
      }
    };

    scheduleNextPoll();

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [study?.studyCode, deletingParticipantIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenReauthSend = async (p: ParticipantNeedingReauth) => {
    setReauthAnchorEl(null);
    const token = await apiService.getAuthToken();
    if (!token || !study?.studyCode) {
      showError(
        "Error",
        "Cannot generate re-auth link. Please try again.",
        "Re-auth",
      );
      return;
    }
    try {
      const isGoogleHealthMigration =
        p.reauthorizationKind === "google_health_migration";
      const response: any = isGoogleHealthMigration
        ? await GoogleHealthService.createReauthorizationInvitation(
            p.participantId,
            study.studyCode,
            token,
          )
        : await FitbitService.getReauthAuthUrl(
            p.participantId,
            study.studyCode,
            token,
          );
      const authUrl = isGoogleHealthMigration
        ? response?.reauthorizationUrl
        : response?.authUrl;
      if (!authUrl) {
        showError("Error", "Failed to get re-auth link.", "Re-auth");
        return;
      }
      setReauthSendParticipant(p);
      setReauthEmailSubject(
        isGoogleHealthMigration
          ? `Action needed: reconnect health data for ${study.name || "your study"}`
          : `Action needed: Re-authorize Fitbit for ${study.name || "your study"}`,
      );
      setReauthEmailBody(
        isGoogleHealthMigration
          ? `Hello,\n\nStudySync is moving this study's Fitbit connection to Google Health. Please reconnect your health-data account using this one-time link:\n\n${authUrl}\n\nThe link expires in 14 days. It will open Google's consent page, where you can approve the StudySync connection. Thank you.`
          : `Hello,\n\nOur study is now collecting new data metrics from Fitbit. To enable these, please re-authorize your Fitbit connection by clicking the link below:\n\n${authUrl}\n\nThis will open Fitbit's consent page where you can grant the new permissions. Thank you.`,
      );
      setReauthSendDialogOpen(true);
    } catch (err: any) {
      showError(
        "Error",
        err?.message || "Failed to get re-auth link.",
        "Re-auth",
      );
    }
  };

  const handleStartSyntheticGoogleHealthTest = async (
    participant: Participant,
  ) => {
    const token = await apiService.getAuthToken();
    if (
      !token ||
      !study?.studyCode ||
      !participant.participantId ||
      participant.studyCode !== study.studyCode ||
      participant.googleHealthSyntheticTest !== true
    ) {
      showError(
        "Google Health test unavailable",
        "This action is limited to the reviewed synthetic test participant.",
        "Google Health",
      );
      return;
    }

    try {
      const response = await GoogleHealthService.startAuthorization(
        participant.participantId,
        study.studyCode,
        token,
      );
      const authorizationUrl = response?.authorizationUrl;
      const parsedUrl = new URL(authorizationUrl);
      if (
        parsedUrl.protocol !== "https:" ||
        parsedUrl.hostname !== "accounts.google.com"
      ) {
        throw new Error("Unexpected Google authorization URL");
      }

      window.location.assign(parsedUrl.toString());
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        showError(
          "Sign in again",
          "Your researcher session has expired. Sign in again, then retry Google Health authorization.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 403) {
        showError(
          "Google Health authorization not approved",
          "This action is limited to the reviewed synthetic participant and a researcher who can manage this study.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 409) {
        showError(
          "Google Health is already authorized",
          "This participant already has an active Google Health connection. Use the adjacent 24-hour read probe to verify access; it does not store health data.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 404) {
        showError(
          "Google Health authorization is not available",
          "The Scale API does not have the approved Google Health authorization route. Verify the API release before retrying.",
          "Google Health",
        );
      } else {
        showError(
          "Google Health authorization failed",
          "The authorization request could not be completed. Retry once; if it continues, review the server audit record without sharing account or health details.",
          "Google Health",
        );
      }
    }
  };

  const handleRunSyntheticGoogleHealthReadProbe = async (
    participant: Participant,
  ) => {
    const token = await apiService.getAuthToken();
    if (
      !token ||
      !study?.studyCode ||
      !participant.participantId ||
      participant.studyCode !== study.studyCode ||
      participant.googleHealthSyntheticTest !== true
    ) {
      showError(
        "Google Health read probe unavailable",
        "This action is limited to the reviewed synthetic test participant.",
        "Google Health",
      );
      return;
    }

    try {
      const result = await GoogleHealthService.runSyntheticReadProbe(
        participant.participantId,
        study.studyCode,
        token,
      );
      const pageLabel = result.pageCount === 1 ? "page" : "pages";
      const pointLabel = result.dataPointCount === 1 ? "point" : "points";
      showSuccess(
        "Google Health read probe completed",
        `Verified Google access with ${result.dataPointCount} ${pointLabel} across ${result.pageCount} ${pageLabel}. This 24-hour test stored no health records.`,
        "Google Health",
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        showError(
          "Sign in again",
          "Your researcher session has expired. Sign in again, then rerun the read probe.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 403) {
        showError(
          "Google Health read probe not approved",
          "The participant must be the reviewed synthetic participant with an active Google Health connection, and you must be allowed to manage this study.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 409) {
        showError(
          "Google Health connection changed",
          "The connection changed while the read probe was running. Start authorization again only if the participant needs to reconnect; otherwise retry the probe.",
          "Google Health",
        );
      } else if (error instanceof ApiError && error.status === 404) {
        showError(
          "Google Health read probe is not available",
          "The approved Scale API release is not serving the read-probe route. Verify the API release before retrying.",
          "Google Health",
        );
      } else {
        showError(
          "Google Health read probe failed",
          "The bounded access check did not complete. Retry once; if it continues, review the server audit record without sharing account or health details.",
          "Google Health",
        );
      }
    }
  };

  const handleSendReauthMessage = async () => {
    if (!study || !reauthSendParticipant?.participantEmail) {
      showError("Error", "No recipient selected.", "Send");
      return;
    }
    if (!reauthEmailSubject.trim() || !reauthEmailBody.trim()) {
      showError(
        "Validation Error",
        "Subject and message are required.",
        "Send",
      );
      return;
    }
    setReauthSending(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError("Authentication Error", "Please sign in again.", "Send");
        return;
      }
      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: [{ email: reauthSendParticipant.participantEmail }],
          emailSubject: reauthEmailSubject,
          emailBody: reauthEmailBody,
          invitationType: "participant",
          emailMode: "contact",
        },
        token,
      );
      if (response.success) {
        setReauthSendDialogOpen(false);
        setReauthSendParticipant(null);
        showSuccess(
          "Message sent",
          "Re-auth message was sent to the participant.",
          "Send",
        );
        await loadStudyData();
      } else {
        showError(
          "Send failed",
          (response as any).error || "Could not send message.",
          "Send",
        );
      }
    } catch (err: any) {
      showError(
        "Send failed",
        (err as any)?.message || "Could not send message.",
        "Send",
      );
    } finally {
      setReauthSending(false);
    }
  };

  const handleContactParticipants = async () => {
    if (
      !study ||
      !contactForm.emailTitle.trim() ||
      !contactForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (contactForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      // Convert participant IDs to emails for sending
      const participantEmails = contactForm.recipients.map((recipient) => {
        const participant = participants.find(
          (p) => p.participantId === recipient,
        );
        return participant ? participant.participantEmail : recipient;
      });

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: participantEmails.map((email) => ({ email })),
          emailSubject: contactReviewForm.emailTitle,
          emailBody: contactReviewForm.content,
          invitationType: "participant",
          emailMode: participantEmailMode,
        },
        token,
      );

      if (response.success) {
        setContactForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setContactReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Emails Sent",
          "Invitation emails have been sent successfully to participants",
          "Send Emails",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Emails",
          response.error || "An error occurred while sending invitation emails",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message || "An unexpected error occurred while sending emails",
        "Send Emails",
      );
    }
  };

  const handleInviteCollaborator = async () => {
    if (
      !study ||
      !inviteCollaboratorForm.emailTitle.trim() ||
      !inviteCollaboratorForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Invitations",
      );
      return;
    }

    if (inviteCollaboratorForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Invitations",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      const response = await EmailService.sendCollaboratorInvitations(
        study.studyCode,
        study.name,
        study.password || "",
        inviteCollaboratorForm.recipients,
        token,
        inviteCollaboratorReviewForm.emailTitle,
        inviteCollaboratorReviewForm.content,
      );

      if (response.success) {
        setContactCollaboratorDialogOpen(false);
        setInviteCollaboratorForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setInviteCollaboratorReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Invitations Sent",
          "Collaborator invitation emails have been sent successfully with pre-filled study information",
          "Send Invitations",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Invitations",
          response.error ||
            "An error occurred while sending invitations to collaborators",
          "Send Invitations",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Invitations",
        err.message || "An unexpected error occurred while sending invitations",
        "Send Invitations",
      );
    }
  };

  const handleContactCollaborator = async () => {
    if (
      !study ||
      !contactCollaboratorForm.emailTitle.trim() ||
      !contactCollaboratorForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (contactCollaboratorForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: contactCollaboratorForm.recipients.map((email) => ({
            email,
          })),
          emailSubject: contactCollaboratorReviewForm.emailTitle,
          emailBody: contactCollaboratorReviewForm.content,
          invitationType: "collaborator",
          emailMode: collaboratorEmailMode,
        },
        token,
      );

      if (response.success) {
        setContactCollaboratorDialogOpen(false);
        setContactCollaboratorForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setContactCollaboratorReviewForm({
          emailTitle: "",
          content: "",
        });
        showSuccess(
          "Emails Sent",
          "Emails have been sent successfully to collaborators",
          "Send Emails",
        );
        await loadStudyData(); // Reload data
      } else {
        showError(
          "Failed to Send Emails",
          response.error ||
            "An error occurred while sending emails to collaborators",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message ||
          "An unexpected error occurred while sending emails to collaborators",
        "Send Emails",
      );
    }
  };

  const handleSendInvitations = async () => {
    if (!fitbitInviteMessage.subject.trim() || !fitbitInviteMessage.body.trim() ||
        /[\r\n]/.test(fitbitInviteMessage.subject)) {
      showError('Validation Error', 'Please enter a subject and invitation message.', 'Send Invitations');
      return;
    }
    if (!study || !studyId || !inviteForm.participants.length) {
      showError(
        "Validation Error",
        "Please add at least one participant",
        "Send Invitations",
      );
      return;
    }

    // Validate all participants have email addresses
    const validParticipants = inviteForm.participants.filter((p) =>
      p.email.trim(),
    );
    if (validParticipants.length === 0) {
      showError(
        "Validation Error",
        "Please enter participant email addresses",
        "Send Invitations",
      );
      return;
    }

    if (!validParticipants.every((p) => EmailService.validateEmail(p.email))) {
      showError(
        "Validation Error",
        "Please enter valid email addresses",
        "Send Invitations",
      );
      return;
    }

    // Validate phone numbers
    const invalidPhoneParticipants = validParticipants.filter(
      (p) => p.phoneNumber && !EmailService.validatePhoneNumber(p.phoneNumber),
    );
    if (invalidPhoneParticipants.length > 0) {
      showError(
        "Validation Error",
        "Please enter valid phone numbers in format (000)-0000-000",
        "Send Invitations",
      );
      return;
    }

    setInviteSending(true);
    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      const invitationsByEmail = new Map<string, InviteParticipant>();
      validParticipants.forEach((participant) => {
        const email = participant.email.trim().toLowerCase();
        if (!invitationsByEmail.has(email)) {
          invitationsByEmail.set(email, { ...participant, email });
        }
      });

      const failedInvitations: InviteParticipant[] = [];
      const alreadyInStudy: { email: string; reason: string }[] = [];
      let sentInvitationCount = 0;
      for (const participant of Array.from(invitationsByEmail.values())) {
        try {
          await issueFitbitCollectionParticipantInvitation(
            studyId,
            participant.email,
            participant.phoneNumber.trim() || null,
            token,
            fitbitInviteMessage,
          );
          sentInvitationCount += 1;
        } catch (error) {
          // Someone already in this study is never given a second ID; say who they are.
          const reason = existingParticipantReason(error);
          if (reason) alreadyInStudy.push({ email: participant.email, reason });
          else failedInvitations.push(participant);
        }
      }

      const outcome = invitationOutcome({ sent: sentInvitationCount, unconfirmed: failedInvitations.length, alreadyInStudy });
      if (outcome) {
        setInviteForm((current) => ({
          ...current,
          participants: failedInvitations.length > 0 ? failedInvitations : [{ email: "", phoneNumber: "" }],
        }));
        showError(outcome.title, outcome.message, "Send Invitations");
        await loadStudyData();
        return;
      }

      setInviteDialogOpen(false);
      setInviteForm({
        studyName: "",
        description: "",
        duration: "",
        participants: [{ email: "", phoneNumber: "" }],
      });
      showSuccess(
        "Invitations Sent",
        `${invitationsByEmail.size} secure Fitbit invitation${
          invitationsByEmail.size === 1 ? "" : "s"
        } sent successfully`,
        "Send Invitations",
      );
      await loadStudyData();
    } catch (err: any) {
      showError(
        "Failed to Send Invitations",
        err.message ||
          "An unexpected error occurred while sending secure Fitbit invitations",
        "Send Invitations",
      );
    } finally {
      setInviteSending(false);
    }
  };

  const handleSendIosInvitations = async () => {
    if (!study || !iosInviteForm.participants.length) {
      showError(
        "Validation Error",
        "Please add at least one participant",
        "Send Invitations",
      );
      return;
    }

    const validParticipants = iosInviteForm.participants.filter((p) =>
      p.email.trim(),
    );
    if (validParticipants.length === 0) {
      showError(
        "Validation Error",
        "Please enter participant email addresses",
        "Send Invitations",
      );
      return;
    }

    if (!validParticipants.every((p) => EmailService.validateEmail(p.email))) {
      showError(
        "Validation Error",
        "Please enter valid email addresses",
        "Send Invitations",
      );
      return;
    }

    const invalidPhoneParticipants = validParticipants.filter(
      (p) => p.phoneNumber && !EmailService.validatePhoneNumber(p.phoneNumber),
    );
    if (invalidPhoneParticipants.length > 0) {
      showError(
        "Validation Error",
        "Please enter valid phone numbers in format (000)-0000-000",
        "Send Invitations",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send invitations",
          "Send Invitations",
        );
        return;
      }

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: validParticipants.map((p) => ({
            email: p.email,
            phoneNumber: p.phoneNumber || undefined,
          })),
          emailSubject: iosInviteReviewForm.emailTitle,
          emailBody: iosInviteReviewForm.content,
          invitationType: "participant",
          emailMode: "invite",
          dataSource: "healthkit",
        },
        token,
      );

      if (response.success) {
        setIosInviteDialogOpen(false);
        setIosInviteMode("qr");
        setIosInviteForm({
          studyName: "",
          description: "",
          duration: "",
          participants: [{ email: "", phoneNumber: "" }],
        });
        setIosInviteReviewForm({
          emailTitle: "",
          content: "",
        });
        setIosInviteReviewOpen(false);
        showSuccess(
          "Emails Sent",
          "Invitation emails have been sent successfully to iOS participants",
          "Send Emails",
        );
        await loadStudyData();
      } else {
        showError(
          "Email Error",
          "Failed to send invitation emails",
          "Send Invitations",
        );
      }
    } catch (_error) {
      showError(
        "Email Error",
        "Failed to send invitation emails",
        "Send Invitations",
      );
    }
  };

  const handleContactIosParticipants = async () => {
    if (
      !study ||
      !iosContactForm.emailTitle.trim() ||
      !iosContactForm.content.trim()
    ) {
      showError(
        "Validation Error",
        "Email title and content are required",
        "Send Emails",
      );
      return;
    }

    if (iosContactForm.recipients.length === 0) {
      showError(
        "Validation Error",
        "Please select at least one recipient",
        "Send Emails",
      );
      return;
    }

    try {
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to send emails",
          "Send Emails",
        );
        return;
      }

      const participantEmails = iosContactForm.recipients.map((recipient) => {
        const participant = participants.find(
          (p) => p.participantId === recipient,
        );
        return participant ? participant.participantEmail : recipient;
      });

      const response = await EmailService.sendInvitations(
        {
          studyCode: study.studyCode,
          studyName: study.name,
          participants: participantEmails.map((email) => ({ email })),
          emailSubject: iosContactReviewForm.emailTitle,
          emailBody: iosContactReviewForm.content,
          invitationType: "participant",
          emailMode: "contact",
          dataSource: "healthkit",
        },
        token,
      );

      if (response.success) {
        setIosInviteDialogOpen(false);
        setIosInviteMode("qr");
        setIosContactForm({
          emailTitle: "",
          content: "",
          recipients: [],
        });
        setIosContactReviewForm({
          emailTitle: "",
          content: "",
        });
        setIosContactReviewOpen(false);
        showSuccess(
          "Emails Sent",
          "Participant emails have been sent successfully",
          "Send Emails",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Send Emails",
          response.error || "An error occurred while sending emails",
          "Send Emails",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Send Emails",
        err.message || "An unexpected error occurred while sending emails",
        "Send Emails",
      );
    }
  };

  // One rule for every screen: active when at least one device is connected.
  const getParticipantStatus = (participant: any) => participantStatus(participant);

  const getParticipantStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "enrolled":
        return "info";
      default:
        return "default";
    }
  };

  const handleDeleteParticipant = async (participantId: string) => {
    if (loading || !participantDeletionConfirmed || participantId !== participantToDelete) return;
    try {
      setLoading(true);

      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to delete participant",
          "Delete Participant",
        );
        return;
      }

      const response = await ParticipantService.deleteParticipant(
        participantId,
        token,
      );

      if (response.success) {
        setDeletingParticipantIds((prev) =>
          prev.includes(participantId) ? prev : [...prev, participantId],
        );
        setDeletionJobsByParticipantId((prev) => ({
          ...prev,
          [participantId]: {
            participantId,
            status: "queued",
            currentStep: "queued",
            progressMessage: "Deletion request queued",
          },
        }));
        analyticsService.trackParticipantDeleted({ studyId: studyId || "" });
        showSuccess(
          "Participant Deletion Started",
          "Participant is being deleted in the background",
          "Delete Participant",
        );
      } else {
        showError(
          "Failed to Delete Participant",
          response.error || "An error occurred while deleting the participant",
          "Delete Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Delete Participant",
        err.message ||
          "An unexpected error occurred while deleting the participant",
        "Delete Participant",
      );
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setParticipantToDelete(null);
    }
  };

  const openDeleteDialog = (participantId: string) => {
    setParticipantDeleteConfirmation("");
    setParticipantToDelete(participantId);
    setDeleteDialogOpen(true);
  };

  const copyStudyCode = async () => {
    if (study?.studyCode) {
      try {
        await navigator.clipboard.writeText(study.studyCode);
        setCopiedStudyCode(study.studyCode);
        setTimeout(() => setCopiedStudyCode(null), 2000);
      } catch (err) {
        // Silently fail - user can try again
      }
    }
  };

  const selectStudySection = (key: StudySectionKey) => {
    const section: StudySection = studySections[key];
    if (section.participantView) setParticipantViewMode(section.participantView);
    selectTabValue(section.tab);
  };
  const navigationItems = studyNavigationItems(tabValue, participantViewMode, selectStudySection,
    <ChatUnreadBadge unreadCount={chatUnreadCount} />);

  // Pending Participants Handlers

  const handleEditPendingParticipant = async () => {
    if (!pendingParticipantToEdit) {
      showError(
        "Validation Error",
        "No participant selected",
        "Edit Participant",
      );
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to edit participant",
          "Edit Participant",
        );
        return;
      }

      const response = await StudyService.updatePendingParticipant(
        study!.id,
        pendingParticipantToEdit.participantId,
        {
          phoneNumber: editPendingForm.phoneNumber.trim() || undefined,
        },
        token,
      );

      if (response.success) {
        setEditPendingDialogOpen(false);
        setEditPendingForm({ phoneNumber: "" });
        setPendingParticipantToEdit(null);
        showSuccess(
          "Participant Updated",
          "Pending participant has been updated successfully",
          "Edit Participant",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Update Participant",
          response.error || "An error occurred while updating the participant",
          "Edit Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Participant",
        err.message ||
          "An unexpected error occurred while updating the participant",
        "Edit Participant",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditParticipant = async () => {
    if (!participantToEdit) {
      showError(
        "Validation Error",
        "No participant selected",
        "Edit Participant",
      );
      return;
    }

    const firstName = editParticipantForm.firstName.trim();
    const lastName = editParticipantForm.lastName.trim();

    if (!firstName || !lastName) {
      showError(
        "Validation Error",
        "First name and last name are required",
        "Edit Participant",
      );
      return;
    }

    try {
      setEditLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to edit participant",
          "Edit Participant",
        );
        return;
      }

      const response = await ParticipantService.updateParticipant(
        participantToEdit.participantId,
        {
          participantEmail: participantToEdit.participantEmail,
          studyCode: participantToEdit.studyCode,
          firstName,
          lastName,
        },
        token,
      );

      if (response.success) {
        setEditParticipantDialogOpen(false);
        setParticipantToEdit(null);
        setEditParticipantForm({ firstName: "", lastName: "" });
        showSuccess(
          "Participant Updated",
          "Participant name has been updated successfully",
          "Edit Participant",
        );
        await loadStudyData();
      } else {
        showError(
          "Failed to Update Participant",
          response.error || "An error occurred while updating the participant",
          "Edit Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Update Participant",
        err.message ||
          "An unexpected error occurred while updating the participant",
        "Edit Participant",
      );
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeletePendingParticipant = async ({ participant }: { participant: PendingParticipant; cancelOnly: boolean }) => {
    const participantId = participant.participantId;
    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to delete participant",
          "Delete Participant",
        );
        return;
      }

      const response = await StudyService.removePendingParticipant(
        study!.id,
        participantId,
        token,
        participant.device,
      );

      if (response.success) {
        await loadStudyData();
        if ((response as { cancelled?: string }).cancelled) {
          // Only that device's invitation was cancelled; the person and their other devices stay.
          showSuccess("Invitation Cancelled", (response as { message?: string }).message || "The invitation was cancelled.",
            "Delete Participant");
        } else if ((response as { deletion?: unknown }).deletion) {
          // A device participant is removed by a background job, not immediately.
          showSuccess(
            "Removal Started",
            "This participant is being removed. It can take several minutes, and the row disappears when it finishes. Use Refresh to check.",
            "Delete Participant",
          );
        } else {
          showSuccess(
            "Participant Removed",
            "Pending participant has been removed successfully",
            "Delete Participant",
          );
        }
      } else {
        showError(
          "Failed to Remove Participant",
          response.error || "An error occurred while removing the participant",
          "Delete Participant",
        );
      }
    } catch (err: any) {
      showError(
        "Failed to Remove Participant",
        err.message ||
          "An unexpected error occurred while removing the participant",
        "Delete Participant",
      );
    } finally {
      setLoading(false);
      setDeletePendingDialogOpen(false);
      setPendingParticipantToDelete(null);
    }
  };

  const openEditPendingDialog = (participant: PendingParticipant) => {
    setPendingParticipantToEdit(participant);
    setEditPendingForm({
      phoneNumber: participant.phoneNumber || "",
    });
    setEditPendingDialogOpen(true);
  };

  const openEditParticipantDialog = (participant: Participant) => {
    setParticipantToEdit(participant);
    setEditParticipantForm({
      firstName: participant.firstName || "",
      lastName: participant.lastName || "",
    });
    setEditParticipantDialogOpen(true);
  };

  const openDeletePendingDialog = (participant: PendingParticipant, cancelOnly: boolean) => {
    setPendingParticipantToDelete({ participant, cancelOnly });
    setDeletePendingDialogOpen(true);
  };

  const openParticipantDetailsDialog = async (participant: Participant) => {
    try {
      setLoading(true);
      const token = await apiService.getAuthToken();
      if (!token) {
        showError(
          "Authentication Error",
          "Authentication required to view participant details",
          "View Details",
        );
        return;
      }

      // Fetch detailed participant information
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/participants/${participant.participantId}/info`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to fetch participant details",
        );
      }

      const participantDetails = await response.json();
      setSelectedParticipantDetails(participantDetails);
      setParticipantDetailsDialogOpen(true);
    } catch (err: any) {
      showError(
        "Failed to Load Details",
        err.message ||
          "An unexpected error occurred while loading participant details",
        "View Details",
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          {/* Skeleton back button */}
          <Skeleton variant="text" width={160} height={36} sx={{ mb: 2 }} />

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Skeleton study info card */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={40}
                    sx={{ mb: 2 }}
                  />
                  <Skeleton variant="text" width="90%" height={24} />
                  <Skeleton
                    variant="text"
                    width="75%"
                    height={24}
                    sx={{ mb: 3 }}
                  />
                  <Skeleton variant="text" width="40%" height={28} />
                </CardContent>
              </Card>
            </Grid>
            {/* Skeleton action buttons */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[...Array(4)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rectangular"
                    height={48}
                    sx={{ borderRadius: 1 }}
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Skeleton tabs */}
          <Paper sx={{ width: "100%", mb: 3 }}>
            <Box sx={{ px: 2, py: 1 }}>
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} variant="text" width={100} height={36} />
                ))}
              </Box>
              {/* Skeleton table rows */}
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={52}
                  sx={{ mb: 1, borderRadius: 0.5 }}
                />
              ))}
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (!study) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error">Study not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/researcher/dashboard")}
              sx={{ alignSelf: { xs: "flex-start", md: "center" } }}
            >
              Back to Dashboard
            </Button>
            {study.fitbitSystemStatus === 'outdated' && <OutdatedStudyNotice />}
          </Box>

          <Grid container spacing={3}>
            {/* Left side - Study info box */}
            <Grid item xs={12} md={8}>
              <Card sx={{ height: "100%" }}>
                <CardContent
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    {/* Line 1: Study name and status */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                          hyphens: "auto",
                          flex: 1,
                          mr: 2,
                        }}
                      >
                        {study.name}
                      </Typography>
                      <Chip
                        label={study.status}
                        color={
                          study.status === "active" ? "success" : "default"
                        }
                        size="medium"
                        sx={{ flexShrink: 0 }}
                      />
                    </Box>

                    {/* Line 2: Study description */}
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      {study.description || "No description provided"}
                    </Typography>
                  </Box>

                  {/* Line 3: Study code with copy icon - positioned at bottom */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: "auto",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                      >
                        Study Code:
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          fontSize: "1.1rem",
                          fontFamily: "monospace",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        {study.studyCode}
                      </Typography>
                      <Tooltip
                        title={
                          copiedStudyCode === study.studyCode
                            ? "Copied!"
                            : "Copy"
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={copyStudyCode}
                          sx={{ p: 0.5 }}
                        >
                          {copiedStudyCode === study.studyCode ? (
                            <CheckCircle fontSize="small" color="success" />
                          ) : (
                            <ContentCopy fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {study.duration && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 500, fontSize: "1.1rem" }}
                        >
                          Study Duration:
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 600,
                            fontSize: "1.1rem",
                            backgroundColor: "primary.50",
                            color: "primary.main",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          {study.duration}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right side - Action buttons */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  height: "100%",
                }}
              >
                {/* Top row: Edit, Refresh, and Check Fitbit Status */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Edit />}
                    onClick={() => setEditDialogOpen(true)}
                    sx={{ py: 1.5, fontSize: "1rem", flex: 1 }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={() => { void loadStudyData(); }}
                    sx={{ py: 1.5, fontSize: "1rem", flex: 1 }}
                  >
                    Refresh
                  </Button>
                </Box>

                {/* Bottom buttons: Invite, Contact, and Template Management */}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Email />}
                    onClick={() => setInviteDialogOpen(true)}
                    sx={{ py: 1.5, fontSize: "0.875rem", flex: 1 }}
                  >
                    Invite Fitbit
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Apple />}
                    onClick={() => setIosInviteDialogOpen(true)}
                    sx={{ py: 1.5, fontSize: "0.875rem", flex: 1 }}
                  >
                    Invite iOS
                  </Button>
                  {ultrahumanEnabled && <Button variant="outlined" startIcon={<Email />} onClick={() => setUltrahumanInviteOpen(true)}
                    sx={{ py: 1.5, fontSize: '0.875rem', flex: 1 }}>Invite Ultrahuman</Button>}
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() => setContactCollaboratorDialogOpen(true)}
                  fullWidth
                  sx={{ py: 1.5, fontSize: "1rem" }}
                >
                  Invite/Contact Collaborator
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Tier-1 Metrics Summary Bar */}
        <StudyMetricsSummary
          studyId={studyId}
          participants={participants}
          pendingParticipants={pendingParticipants}
          loading={false}
          onMetricClick={(tabIndex, participantView) => {
            if (participantView) {
              setParticipantViewMode(participantView);
            }
            selectTabValue(tabIndex);
          }}
        />

        {/* Tabs */}
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            <StudyNavigation value={activeTabGroup} items={navigationItems} />

            {/* Participants Tab */}
            <TabPanel value={tabValue} index={studySections.participants.tab} participantView={participantViewMode}>
              {participantViewMode === "participant" ? (
                participants.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <People
                      sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                    />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      No Participants Yet
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Send email invitations to start enrolling participants in
                      your study.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Email />}
                      onClick={() => setInviteDialogOpen(true)}
                    >
                      Send Invitations
                    </Button>
                  </Box>
                ) : (
                  <>
                    {participantsNeedingReauth.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          mb: 1,
                        }}
                      >
                        <Tooltip title="Participants needing a health-data reauthorization. Click to see the list and send a message.">
                          <IconButton
                            color="warning"
                            onClick={(e) => setReauthAnchorEl(e.currentTarget)}
                            size="medium"
                            aria-label="Participants needing re-auth"
                          >
                            <Badge
                              badgeContent={participantsNeedingReauth.length}
                              color="error"
                            >
                              <Warning />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                    <Popover
                      open={Boolean(reauthAnchorEl)}
                      anchorEl={reauthAnchorEl}
                      onClose={() => setReauthAnchorEl(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <Box sx={{ p: 2, minWidth: 320 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Participants needing re-authorization
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Send each participant the reauthorization link that
                          matches their connection: a Fitbit scope update or,
                          after the controlled migration is enabled, a one-time
                          Google Health migration link.
                        </Typography>
                        <List dense disablePadding>
                          {participantsNeedingReauth.map((p) => {
                            const displayName =
                              [p.firstName, p.lastName]
                                .filter(Boolean)
                                .join(" ") ||
                              p.participantEmail ||
                              p.participantId;
                            return (
                              <ListItem
                                key={p.participantId}
                                disablePadding
                                secondaryAction={
                                  <Button
                                    size="small"
                                    startIcon={<Send />}
                                    onClick={() => handleOpenReauthSend(p)}
                                    variant="outlined"
                                  >
                                    Send message
                                  </Button>
                                }
                              >
                                <ListItemText
                                  primary={displayName}
                                  secondary={[
                                    p.reauthorizationKind === "google_health_migration"
                                      ? "Google Health migration required"
                                      : "Fitbit permission update required",
                                    p.participantEmail &&
                                    p.participantEmail !== displayName
                                      ? p.participantEmail
                                      : null,
                                  ].filter(Boolean).join(" · ")}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    </Popover>
                    <TableContainer sx={researcherTableContainerSx}>
                      <Table sx={researcherTableSx}>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "left",
                                width: "20%",
                              }}
                            >
                              Name
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "left",
                                width: "20%",
                              }}
                            >
                              Email
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "left",
                                width: "15%",
                              }}
                            >
                              Phone
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "center",
                                width: "10%",
                              }}
                            >
                              Status
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "center",
                                width: "10%",
                              }}
                            >
                              Data Source
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "center",
                                width: "12%",
                              }}
                            >
                              Enrolled Date
                            </TableCell>
                            <TableCell
                              sx={{
                                ...researcherTableHeaderSx,
                                textAlign: "center",
                                width: "13%",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {participants.map((participant) => {
                            const participantKey =
                              participant.id || participant.participantId;
                            const deletionJob =
                              deletionJobsByParticipantId[participantKey];
                            const historicalProgress = participant.fitbitCollectionEnrollment
                              ? null : getHistoricalProgressInfo(participant);

                            return (
                              <React.Fragment key={participantKey}>
                              <TableRow>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.75,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      color="text.primary"
                                      sx={researcherNameSx}
                                    >
                                      {participant.firstName &&
                                      participant.lastName
                                        ? `${participant.firstName} ${participant.lastName}`
                                        : participant.firstName ||
                                          participant.lastName ||
                                          "N/A"}
                                    </Typography>
                                  </Box>
                                  {participant.participantId && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        ...researcherMetaSx,
                                        display: "block",
                                        mt: 0.5,
                                      }}
                                      title={participant.participantId}
                                    >
                                      ID: {participant.participantId}
                                    </Typography>
                                  )}
                                  {historicalProgress && (
                                    <Tooltip
                                      title={historicalProgress.tooltip}
                                      arrow
                                    >
                                      <Box sx={{ mt: 1.25, maxWidth: 180 }}>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            mb: 0.5,
                                          }}
                                        >
                                          <Typography
                                            variant="caption"
                                            color={
                                              historicalProgress.kind ===
                                              "failed"
                                                ? "error.main"
                                                : "text.secondary"
                                            }
                                            sx={{ fontWeight: 600 }}
                                          >
                                            {historicalProgress.label}
                                          </Typography>
                                        </Box>
                                        <LinearProgress
                                          variant="determinate"
                                          value={historicalProgress.percentage}
                                          color={
                                            historicalProgress.kind === "failed"
                                              ? "error"
                                              : participant.historicalExtraction
                                                    ?.status === "completed"
                                                ? "success"
                                                : "primary"
                                          }
                                          sx={{
                                            height: 6,
                                            borderRadius: 999,
                                            backgroundColor: "action.hover",
                                          }}
                                        />
                                      </Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.participantEmail || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ py: 1.5, textAlign: "left" }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.phoneNumber || "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <Chip variant="outlined"
                                      label={
                                        deletingParticipantIds.includes(
                                          participantKey,
                                        )
                                          ? "deleting"
                                          : getParticipantStatus(participant)
                                      }
                                      color={
                                        deletingParticipantIds.includes(
                                          participantKey,
                                        )
                                          ? "default"
                                          : getParticipantStatusColor(
                                              getParticipantStatus(participant),
                                            )
                                      }
                                      size="small"
                                    />
                                    {deletingParticipantIds.includes(
                                      participantKey,
                                    ) &&
                                      deletionJob?.progressMessage && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{
                                            display: "block",
                                            mt: 0.5,
                                            maxWidth: 180,
                                            lineHeight: 1.2,
                                            textAlign: "center",
                                          }}
                                        >
                                          {deletionJob.progressMessage}
                                        </Typography>
                                      )}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {formatDeviceSources(participant)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {participant.enrolledAt
                                      ? new Date(
                                          participant.enrolledAt,
                                        ).toLocaleDateString()
                                      : "N/A"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ py: 1.5, textAlign: "center" }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        openEditParticipantDialog(participant)
                                      }
                                      title="Edit Participant Name"
                                      disabled={Boolean(participant.deletion)}
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="info"
                                      onClick={() =>
                                        openParticipantDetailsDialog(
                                          participant,
                                        )
                                      }
                                      title="View Details"
                                    >
                                      <Visibility />
                                    </IconButton>
                                    {participant.fitbitCollectionEnrollment || participant.ultrahumanCollectionEnrollment ? (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setFitbitParticipantToDelete(participant)}
                                        aria-label={`${participant.deletion ? 'View deletion progress for' : 'Delete'} ${[participant.firstName, participant.lastName].filter(Boolean).join(' ') || participantKey}`}
                                        title={participant.deletion
                                          ? (participant.deletion.status === 'failed' ? 'Retry deletion' : 'View deletion progress')
                                          : 'Delete participant'}
                                      >
                                        <Delete />
                                      </IconButton>
                                    ) : (
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          openDeleteDialog(participant.id)
                                        }
                                        title="Delete"
                                        disabled={deletingParticipantIds.includes(
                                          participantKey,
                                        )}
                                      >
                                        <Delete />
                                      </IconButton>
                                    )}
                                    {participant.googleHealthSyntheticTest ===
                                      true &&
                                      (
                                        !isActiveSyntheticGoogleHealthParticipant(
                                          participant,
                                          study?.studyCode,
                                        ) ||
                                        !GOOGLE_HEALTH_UI_CONFIG.SYNTHETIC_SCOPE_UPGRADE_ENABLED
                                      ) && (
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={() =>
                                            handleStartSyntheticGoogleHealthTest(
                                              participant,
                                            )
                                          }
                                          title="Authorize or reconnect Google Health"
                                          disabled={deletingParticipantIds.includes(
                                            participantKey,
                                          )}
                                        >
                                          <Launch />
                                        </IconButton>
                                      )}
                                    {participant.googleHealthSyntheticTest ===
                                      true && (
                                        <IconButton
                                          size="small"
                                          color="secondary"
                                          onClick={() =>
                                            handleRunSyntheticGoogleHealthReadProbe(
                                              participant,
                                            )
                                          }
                                          title="Run the 24-hour read-only Google Health probe"
                                          disabled={deletingParticipantIds.includes(
                                            participantKey,
                                          )}
                                        >
                                          <Refresh />
                                        </IconButton>
                                      )}
                                    <GoogleHealthScopeUpgradeButton
                                      participant={participant}
                                      studyCode={study?.studyCode}
                                      enabled={
                                        GOOGLE_HEALTH_UI_CONFIG.SYNTHETIC_SCOPE_UPGRADE_ENABLED
                                      }
                                      getAuthToken={() => apiService.getAuthToken()}
                                      showError={showError}
                                      disabled={deletingParticipantIds.includes(
                                        participantKey,
                                      )}
                                    />
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {(participant.fitbitCollectionEnrollment || participant.ultrahumanCollectionEnrollment) && studyId && !participant.deletion && !deletingParticipantIds.includes(participantKey) && (
                                <TableRow>
                                  <TableCell colSpan={7} sx={{ pt: 0, pb: 1.5 }}>
                                    {invitedSources(participant).map(source => (
                                      <Typography key={`${source}-invited`} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {sourceLabel(source)}: invitation sent, waiting for the participant to connect.
                                      </Typography>
                                    ))}
                                    {historySources(participant)
                                      .map(source => <CollectionSourceProvider key={source} source={source}>
                                        <ParticipantHistoryProgress studyId={studyId} participantId={participant.participantId || participant.id}
                                          name={[participant.firstName, participant.lastName].filter(Boolean).join(' ') || participant.participantId || participant.id} />
                                      </CollectionSourceProvider>)}
                                  </TableCell>
                                </TableRow>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )
              ) : (
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 3,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Participants are added here when you send invitations
                    </Typography>
                  </Box>

                  {pendingParticipants.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4 }}>
                      <People
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      />
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        No Pending Participants
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 3 }}
                      >
                        Send email invitations to add participants to the study
                        roster.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<Email />}
                        onClick={() => setInviteDialogOpen(true)}
                      >
                        Send Invitations
                      </Button>
                    </Box>
                  ) : (
                    <PendingParticipantsTable participants={pendingParticipants} onEdit={openEditPendingDialog} onRemove={openDeletePendingDialog} />
                  )}
                </Box>
              )}
            </TabPanel>

            <TabPanel value={tabValue} index={studySections.members.tab}>
              <StudyMembersTab members={studyMembers} />
            </TabPanel>

            {/* Email History Tab */}
            <TabPanel value={tabValue} index={studySections.email.tab}>
              <EmailHistoryTab history={emailHistory} error={emailHistoryError} loading={loading}
                onRefresh={() => { void loadStudyData(); }} onView={email => { setSelectedEmail(email); setEmailViewOpen(true); }} />
            </TabPanel>

            {/* Templates moved to standalone page */}

            {/* Data Extraction Tab */}
            <TabPanel value={tabValue} index={studySections.extraction.tab}>
              <StudyDataExport studyId={studyId!} source={collectionSource} onSourceChange={setCollectionSource} outdated={study.fitbitSystemStatus === 'outdated'} legacy={<DataExtractionInterface
                studyId={studyId!}
                studyName={study?.name || "Study"}
                participants={participants.map((p) => ({
                  id: p.id,
                  participantId: p.participantId,
                  participantEmail: p.participantEmail,
                  firstName: p.firstName,
                  lastName: p.lastName,
                  phoneNumber: p.phoneNumber,
                  authorizationStatus: p.authorizationStatus || false,
                  fitbitCollectionEnrollment: p.fitbitCollectionEnrollment === true,
                  hasTokens: !!(p.fitbitTokens?.accessToken || (p as any).fitbitData?.accessToken || (p as any).fitbitAccessToken),
                  fitbitTokens: p.fitbitTokens || ((p as any).fitbitData ? {
                    accessToken: (p as any).fitbitData?.accessToken,
                    expiresAt: (p as any).fitbitData?.expiresAt,
                  } : undefined),
                  fitbitData: (p as any).fitbitData,
                  fitbitAccessToken: (p as any).fitbitAccessToken,
                  fitbitUserId: p.fitbitUserId,
                  healthkit: p.healthkit,
                  enrolledAt: p.enrolledAt,
                  historicalExtraction: p.historicalExtraction,
                }))}
                onRefreshParticipants={loadStudyData}
              />} />
            </TabPanel>

            {ultrahumanEnabled && [studySections.visualization.tab, studySections.coverage.tab, studySections.adherence.tab, alertsTabIndex].includes(tabValue) &&
              <Box sx={{ px: 2, pt: 2 }}><CollectionSourcePicker value={collectionSource} onChange={setCollectionSource} /></Box>}
            <CollectionSourceProvider source={collectionSource} key={`${studyId}:${collectionSource}`}>
              <StudySectionPanels key={`${studyId}:${user?.uid}`} studyId={studyId!} tab={tabValue} />
            </CollectionSourceProvider>

            {/* Alerts Tab */}
            <TabPanel value={tabValue} index={alertsTabIndex}>
              <Box sx={{ p: 2 }}>
                <CollectionSourceProvider source={collectionSource} key={collectionSource}>
                  <FitbitCollectionDataView mode="alerts" studyId={studyId!} legacy={<AlertsTab studyId={studyId!} />} />
                </CollectionSourceProvider>
              </Box>
            </TabPanel>

            {/* Tracking ID Tab */}
            <TabPanel value={tabValue} index={trackingIdTabIndex}>
              <TrackingIdTab
                participants={participants}
                studyId={studyId || ""}
                onRefresh={loadStudyData}
              />
            </TabPanel>
            <Box role="tabpanel" hidden={tabValue !== CHAT_TAB_INDEX} aria-label="Chat" sx={{ p: { xs: 1, sm: 2 } }}>
              <ChatTab key={`${studyId}:${user?.uid}`} studyId={studyId || ''}
                isOwner={Boolean(user?.uid && study?.researcherId === user.uid)}
                participantNames={Object.fromEntries(participants.map(participant => [participant.participantId,
                  [participant.firstName, participant.lastName].filter(Boolean).join(' ') || participant.trackingId || participant.participantId]))}
                active={tabValue === CHAT_TAB_INDEX} onUnreadCountChange={setChatUnreadCount} />
            </Box>
          </Paper>
          {ultrahumanInviteOpen && studyId && <UltrahumanInviteDialog studyId={studyId} onClose={() => setUltrahumanInviteOpen(false)} onCreated={() => { void loadStudyData({ background: true }); }} />}
        </Box>

        {/* Invite/Contact Participants Dialog */}
        <Dialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {participantEmailMode === "invite"
                  ? "Invite Participants"
                  : "Contact Participants"}
              </Typography>
              <ToggleButtonGroup
                value={participantEmailMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setParticipantEmailMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {participantEmailMode === "invite"
                ? "Send each participant a secure, one-time Fitbit enrollment link. StudySync creates and delivers the link; it is never exposed in this dashboard."
                : "Send an email to study participants. You can select existing participants or enter email addresses manually."}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {participantEmailMode === "invite" ? (
                <>
                  <TextField label="Email subject" fullWidth required
                    value={fitbitInviteMessage.subject} inputProps={{ maxLength: 200 }}
                    onChange={event => setFitbitInviteMessage(current => ({ ...current, subject: event.target.value }))} />
                  <TextField label="Invitation message" fullWidth required multiline rows={5}
                    value={fitbitInviteMessage.body} inputProps={{ maxLength: 10000 }}
                    onChange={event => setFitbitInviteMessage(current => ({ ...current, body: event.target.value }))} />
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="subtitle2">Email preview</Typography>
                    <Typography sx={{ mt: 1, fontWeight: 600 }}>{fitbitInviteMessage.subject}</Typography>
                    <Typography sx={{ mt: 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{fitbitInviteMessage.body}</Typography>
                    <Typography sx={{ mt: 2 }}>Open your personal StudySync link to connect Fitbit or resume your unfinished profile:</Typography>
                    <Typography color="primary">[Personal enrollment link]</Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>This invitation expires at [expiration date].</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">Each email includes its own private link and expiration date, added automatically when sent.</Typography>

                  <ParticipantInviteList
                    participants={inviteForm.participants}
                    onChange={(participants) => setInviteForm((prev) => ({ ...prev, participants }))}
                  />
                </>
              ) : (
                <ParticipantContactForm
                  form={contactForm}
                  setForm={setContactForm}
                  participants={participants}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (participantEmailMode === "invite") {
                  void handleSendInvitations();
                } else {
                  // Contact mode - populate review form with contact form content
                  setContactReviewForm({
                    emailTitle: contactForm.emailTitle,
                    content: contactForm.content,
                  });
                  setContactReviewOpen(true);
                }
              }}
              variant="contained"
              disabled={
                inviteSending ||
                (participantEmailMode === "invite"
                  ? inviteForm.participants.length === 0 ||
                    !fitbitInviteMessage.subject.trim() || !fitbitInviteMessage.body.trim() ||
                    !inviteForm.participants.some((p) => p.email.trim())
                  : !contactForm.emailTitle.trim() ||
                    !contactForm.content.trim() ||
                    contactForm.recipients.length === 0)
              }
              startIcon={
                participantEmailMode === "invite" && inviteSending
                  ? <CircularProgress size={18} color="inherit" />
                  : undefined
              }
            >
              {participantEmailMode === "invite"
                ? inviteSending
                  ? "Sending..."
                  : "Send Invitations"
                : "Continue to Review"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite iOS Participants Dialog */}
        <Dialog
          open={iosInviteDialogOpen}
          onClose={() => {
            setIosInviteDialogOpen(false);
            setIosInviteMode("qr");
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {iosInviteMode === "contact" ? "Contact iOS" : "Invite iOS"}
              </Typography>
              <ToggleButtonGroup
                value={iosInviteMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setIosInviteMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="qr">QR Code</ToggleButton>
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            {iosInviteMode === "qr" && (
              <>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Share this QR code or link with participants to enroll via the
                  iOS app. This is for Apple Health (HealthKit) data collection
                  only.
                </Typography>
                {study ? (
                  <HealthKitQRGenerator
                    studyCode={study.studyCode}
                    studyName={study.name}
                  />
                ) : (
                  <Alert severity="info">
                    Study details are still loading. Refresh and try again.
                  </Alert>
                )}
              </>
            )}

            {iosInviteMode === "invite" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Send email invitations to iOS participants. They will receive
                  a link to download the StudySync app and enroll with study
                  code {study?.studyCode}.
                </Typography>
                <ParticipantInviteForm form={iosInviteForm} setForm={setIosInviteForm} study={study} />
              </Box>
            )}

            {iosInviteMode === "contact" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Send contact emails to iOS participants. You can select
                  existing participants or enter additional email addresses
                  manually.
                </Typography>
                <ParticipantContactForm form={iosContactForm} setForm={setIosContactForm} participants={participants} />
              </Box>
            )}
          </DialogContent>
          {iosInviteMode !== "qr" && (
            <DialogActions>
              <Button
                onClick={() => {
                  setIosInviteDialogOpen(false);
                  setIosInviteMode("qr");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (iosInviteMode === "invite") {
                    const emailTitle = `Invitation to participate in ${
                      iosInviteForm.studyName || study?.name
                    }`;
                    const emailContent = `Dear Participant,

You are invited to participate in our research study: ${
                      iosInviteForm.studyName || study?.name
                    }

${iosInviteForm.description || study?.description || "No description provided"}

Duration: ${iosInviteForm.duration || "Not specified"}

To participate, please download the StudySync iOS app and use the link below to enroll with your Apple Health data.

Study Code: ${study?.studyCode}

Thank you for your interest in participating in our research.

Best regards,
Research Team`;

                    setIosInviteReviewForm({
                      emailTitle,
                      content: emailContent,
                    });
                    setIosInviteReviewOpen(true);
                  } else {
                    setIosContactReviewForm({
                      emailTitle: iosContactForm.emailTitle,
                      content: iosContactForm.content,
                    });
                    setIosContactReviewOpen(true);
                  }
                }}
                variant="contained"
                disabled={
                  iosInviteMode === "invite"
                    ? iosInviteForm.participants.length === 0 ||
                      !iosInviteForm.participants.some((p) => p.email.trim())
                    : !iosContactForm.emailTitle.trim() ||
                      !iosContactForm.content.trim() ||
                      iosContactForm.recipients.length === 0
                }
              >
                Continue to Review
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* iOS Email Invitation Review Dialog */}
        <Dialog
          open={iosInviteReviewOpen}
          onClose={() => setIosInviteReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review iOS Invitation Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the email before sending. The enrollment deep link
              will be appended automatically by the server.
            </Typography>
            <TextField
              label="Email Subject"
              fullWidth
              variant="outlined"
              value={iosInviteReviewForm.emailTitle}
              onChange={(e) =>
                setIosInviteReviewForm((prev) => ({
                  ...prev,
                  emailTitle: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email Content"
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              value={iosInviteReviewForm.content}
              onChange={(e) =>
                setIosInviteReviewForm((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosInviteReviewOpen(false)}>Back</Button>
            <Button
              onClick={() => {
                handleSendIosInvitations();
              }}
              variant="contained"
              disabled={
                !iosInviteReviewForm.emailTitle.trim() ||
                !iosInviteReviewForm.content.trim()
              }
            >
              Send Invitations
            </Button>
          </DialogActions>
        </Dialog>

        {/* iOS Contact Email Review Dialog */}
        <Dialog
          open={iosContactReviewOpen}
          onClose={() => setIosContactReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review iOS Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and edit the email before sending.
            </Typography>
            <TextField
              label="Email Subject"
              fullWidth
              variant="outlined"
              value={iosContactReviewForm.emailTitle}
              onChange={(e) =>
                setIosContactReviewForm((prev) => ({
                  ...prev,
                  emailTitle: e.target.value,
                }))
              }
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email Content"
              fullWidth
              multiline
              rows={10}
              variant="outlined"
              value={iosContactReviewForm.content}
              onChange={(e) =>
                setIosContactReviewForm((prev) => ({
                  ...prev,
                  content: e.target.value,
                }))
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosContactReviewOpen(false)}>Back</Button>
            <Button
              onClick={() => {
                handleContactIosParticipants();
              }}
              variant="contained"
              disabled={
                !iosContactReviewForm.emailTitle.trim() ||
                !iosContactReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send re-auth message to participant */}
        <Dialog
          open={reauthSendDialogOpen}
          onClose={() => {
            if (!reauthSending) {
              setReauthSendDialogOpen(false);
              setReauthSendParticipant(null);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Send re-auth message
            {reauthSendParticipant && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
                sx={{ display: "block", fontWeight: "normal", mt: 0.5 }}
              >
                To: {reauthSendParticipant.participantEmail}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <TextField
              label="Subject"
              fullWidth
              variant="outlined"
              value={reauthEmailSubject}
              onChange={(e) => setReauthEmailSubject(e.target.value)}
              sx={{ mt: 1, mb: 2 }}
            />
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={8}
              variant="outlined"
              value={reauthEmailBody}
              onChange={(e) => setReauthEmailBody(e.target.value)}
              helperText="The re-auth link is included above. You can edit the message before sending."
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setReauthSendDialogOpen(false);
                setReauthSendParticipant(null);
              }}
              disabled={reauthSending}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendReauthMessage}
              disabled={
                reauthSending ||
                !reauthEmailSubject.trim() ||
                !reauthEmailBody.trim()
              }
              startIcon={reauthSending ? undefined : <Send />}
            >
              {reauthSending ? "Sending…" : "Send message"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite/Contact Collaborator Dialog */}
        <Dialog
          open={contactCollaboratorDialogOpen}
          onClose={() => {
            setContactCollaboratorDialogOpen(false);
            // Reset forms when dialog is closed
            setInviteCollaboratorForm({
              emailTitle: "",
              content: "",
              recipients: [],
            });
            setContactCollaboratorForm({
              emailTitle: "",
              content: "",
              recipients: [],
            });
            setCollaboratorEmailMode("invite"); // Reset to invite mode
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">
                {collaboratorEmailMode === "invite"
                  ? "Invite Collaborators"
                  : "Contact Collaborators"}
              </Typography>
              <ToggleButtonGroup
                value={collaboratorEmailMode}
                exclusive
                onChange={(event, newMode) => {
                  if (newMode !== null) {
                    setCollaboratorEmailMode(newMode);
                  }
                }}
                size="small"
              >
                <ToggleButton value="invite">Invite</ToggleButton>
                <ToggleButton value="contact">Contact</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {collaboratorEmailMode === "invite"
                ? "Invite new collaborators to join this study. They will receive an invitation email with pre-filled study code and password for easy joining."
                : "Send an email to existing study collaborators. You can enter collaborator email addresses manually."}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Title"
                fullWidth
                variant="outlined"
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.emailTitle
                    : contactCollaboratorForm.emailTitle
                }
                onChange={(e) => {
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      emailTitle: e.target.value,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      emailTitle: e.target.value,
                    }));
                  }
                }}
                placeholder={
                  collaboratorEmailMode === "invite"
                    ? "Enter invitation subject"
                    : "Enter email subject"
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.content
                    : contactCollaboratorForm.content
                }
                onChange={(e) => {
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }));
                  }
                }}
                placeholder={
                  collaboratorEmailMode === "invite"
                    ? "Enter your invitation message..."
                    : "Enter your message to collaborators..."
                }
                required
              />

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Recipients
              </Typography>

              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={
                  collaboratorEmailMode === "invite"
                    ? inviteCollaboratorForm.recipients
                    : contactCollaboratorForm.recipients
                }
                onChange={(event, newValue) => {
                  const validEmails = newValue.filter((email: string) => {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    return emailRegex.test(email);
                  });
                  if (collaboratorEmailMode === "invite") {
                    setInviteCollaboratorForm((prev) => ({
                      ...prev,
                      recipients: validEmails,
                    }));
                  } else {
                    setContactCollaboratorForm((prev) => ({
                      ...prev,
                      recipients: validEmails,
                    }));
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      collaboratorEmailMode === "invite"
                        ? "Collaborator Email Addresses to Invite"
                        : "Collaborator Email Addresses"
                    }
                    placeholder="Enter email addresses (press Enter after each)"
                    helperText="Enter valid email addresses like collaborator@example.com"
                    required
                    autoFocus
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactCollaboratorDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (collaboratorEmailMode === "invite") {
                  // Populate review form with invite collaborator form content
                  // If no custom content, use default template
                  const defaultContent = inviteCollaboratorForm.content.trim()
                    ? inviteCollaboratorForm.content
                    : `Dear Colleague,\n\nYou have been invited to collaborate on the research study: ${
                        study?.name || "this study"
                      }\n\nBest regards,\nThe Research Team`;

                  setInviteCollaboratorReviewForm({
                    emailTitle:
                      inviteCollaboratorForm.emailTitle ||
                      `Collaborator Invitation: ${study?.name || "Study"}`,
                    content: defaultContent,
                  });
                  setInviteCollaboratorReviewOpen(true);
                } else {
                  // Populate review form with contact collaborator form content
                  setContactCollaboratorReviewForm({
                    emailTitle: contactCollaboratorForm.emailTitle,
                    content: contactCollaboratorForm.content,
                  });
                  setContactCollaboratorReviewOpen(true);
                }
              }}
              variant="contained"
              disabled={
                collaboratorEmailMode === "invite"
                  ? !inviteCollaboratorForm.emailTitle.trim() ||
                    !inviteCollaboratorForm.content.trim()
                  : !contactCollaboratorForm.emailTitle.trim() ||
                    !contactCollaboratorForm.content.trim()
              }
            >
              Continue to Review
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Review Dialog */}
        <Dialog
          open={contactReviewOpen}
          onClose={() => setContactReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the contact email before sending. You can modify
              both the title and content.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Subject"
                fullWidth
                variant="outlined"
                value={contactReviewForm.emailTitle}
                onChange={(e) =>
                  setContactReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={contactReviewForm.content}
                onChange={(e) =>
                  setContactReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                required
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {contactForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setContactReviewOpen(false);
                handleContactParticipants();
              }}
              variant="contained"
              disabled={
                !contactReviewForm.emailTitle.trim() ||
                !contactReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        {/* Invite Collaborator Review Dialog */}
        <Dialog
          open={inviteCollaboratorReviewOpen}
          onClose={() => setInviteCollaboratorReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Collaborator Invitation Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the collaborator invitation email before sending.
              The study code and password will be automatically included in the
              email. Collaborators will be instructed to use the research
              dashboard to join the study.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Title"
                fullWidth
                variant="outlined"
                value={inviteCollaboratorReviewForm.emailTitle}
                onChange={(e) =>
                  setInviteCollaboratorReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                placeholder="Enter invitation subject"
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={8}
                variant="outlined"
                value={inviteCollaboratorReviewForm.content}
                onChange={(e) =>
                  setInviteCollaboratorReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                placeholder="Enter your invitation message..."
                required
              />

              {/* Study Information Display */}
              {study && (
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Study Information (will be included in email):
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Study Name:</strong> {study.name}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Study Code:</strong> {study.studyCode}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Study Password:</strong>{" "}
                    {study.password || "No password set"}
                  </Typography>
                </Box>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {inviteCollaboratorForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInviteCollaboratorReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setInviteCollaboratorReviewOpen(false);
                handleInviteCollaborator();
              }}
              variant="contained"
              disabled={
                !inviteCollaboratorReviewForm.emailTitle.trim() ||
                !inviteCollaboratorReviewForm.content.trim()
              }
            >
              Send Invitation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Contact Collaborator Review Dialog */}
        <Dialog
          open={contactCollaboratorReviewOpen}
          onClose={() => setContactCollaboratorReviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Review & Edit Collaborator Contact Email</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review and edit the collaborator contact email before sending. You
              can modify both the title and content.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email Subject"
                fullWidth
                variant="outlined"
                value={contactCollaboratorReviewForm.emailTitle}
                onChange={(e) =>
                  setContactCollaboratorReviewForm((prev) => ({
                    ...prev,
                    emailTitle: e.target.value,
                  }))
                }
                required
              />

              <TextField
                label="Email Content"
                fullWidth
                multiline
                rows={12}
                variant="outlined"
                value={contactCollaboratorReviewForm.content}
                onChange={(e) =>
                  setContactCollaboratorReviewForm((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                required
              />
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Recipients: {contactCollaboratorForm.recipients.join(", ")}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setContactCollaboratorReviewOpen(false)}>
              Back to Edit
            </Button>
            <Button
              onClick={() => {
                setContactCollaboratorReviewOpen(false);
                handleContactCollaborator();
              }}
              variant="contained"
              disabled={
                !contactCollaboratorReviewForm.emailTitle.trim() ||
                !contactCollaboratorReviewForm.content.trim()
              }
            >
              Send Email
            </Button>
          </DialogActions>
        </Dialog>

        <StudyEmailDetailsDialog
          open={emailViewOpen}
          email={selectedEmail}
          onClose={() => setEmailViewOpen(false)}
        />

        {editDialogOpen && study && (
          <StudyEditDialog
            key={study.id}
            study={study}
            layout="detailed"
            onClose={() => setEditDialogOpen(false)}
            onError={showError}
            onSaved={async (values) => {
              setStudy(current => current?.id === study.id ? { ...current, ...values } : current);
              setEditDialogOpen(false);
              showSuccess("Study Updated", "Study information has been updated successfully", "Update Study");
              await loadStudyData();
            }}
          />
        )}

        {fitbitParticipantToDelete && (
          <ParticipantDeletionDialog key={fitbitParticipantToDelete.id} participant={fitbitParticipantToDelete}
            onClose={() => setFitbitParticipantToDelete(null)}
            onAccepted={job => {
              const deletion = { jobId: job.jobId, status: job.status };
              setParticipants(current => current.map(participant => (participant.id || participant.participantId) === job.participantId
                ? { ...participant, deletion } : participant));
              setFitbitParticipantToDelete(current => current ? { ...current, deletion } : null);
            }}
            onDeleted={() => { setFitbitParticipantToDelete(null); void loadStudyData(); }} />
        )}
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Delete Participant</DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete this participant?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone. All participant data will be
              permanently removed.
            </Typography>
            <DeletionConfirmation key={participantDeletionId} kind="participant" expectedValue={participantDeletionId}
              value={participantDeleteConfirmation} onChange={setParticipantDeleteConfirmation} disabled={loading} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={() =>
                participantToDelete &&
                handleDeleteParticipant(participantToDelete)
              }
              disabled={loading || !participantDeletionConfirmed}
              variant="contained"
              color="error"
            >
              Delete Participant
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Pending Participant Dialog */}
        <Dialog
          open={editPendingDialogOpen}
          onClose={() => setEditPendingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Pending Participant</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update the participant's information.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Phone Number (Optional)"
                fullWidth
                variant="outlined"
                value={editPendingForm.phoneNumber}
                onChange={(e) =>
                  setEditPendingForm((prev) => ({
                    ...prev,
                    phoneNumber: formatPhoneNumber(e.target.value),
                  }))
                }
                placeholder="(000)-0000-000"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditPendingDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditPendingParticipant}
              variant="contained"
              disabled={editLoading}
            >
              {editLoading ? "Updating..." : "Update Participant"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={editParticipantDialogOpen}
          onClose={() => setEditParticipantDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Participant Name</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Update the participant&apos;s first and last name.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                variant="outlined"
                value={editParticipantForm.firstName}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
              />
              <TextField
                label="Last Name"
                fullWidth
                variant="outlined"
                value={editParticipantForm.lastName}
                onChange={(e) =>
                  setEditParticipantForm((prev) => ({
                    ...prev,
                    lastName: e.target.value,
                  }))
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditParticipantDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleEditParticipant}
              variant="contained"
              disabled={editLoading}
            >
              {editLoading ? "Updating..." : "Update Participant"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Pending Participant Dialog */}
        <Dialog
          open={deletePendingDialogOpen}
          onClose={() => setDeletePendingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          {pendingParticipantToDelete?.cancelOnly && pendingParticipantToDelete.participant.device ? <>
            <DialogTitle>Cancel {sourceLabel(pendingParticipantToDelete.participant.device)} Invitation</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Cancel the waiting {sourceLabel(pendingParticipantToDelete.participant.device)} invitation for{" "}
                {pendingParticipantToDelete.participant.email || pendingParticipantToDelete.participant.participantId}?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Only this invitation and its link are cancelled. The participant stays in the study, and their other
                devices and data are not affected. You can send a new invitation later.
              </Typography>
            </DialogContent>
          </> : <>
            <DialogTitle>Remove Pending Participant</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to remove this participant from the study
                roster?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Their pending enrollment and existing invitation links will be removed.
                You can send a new invitation later.
              </Typography>
            </DialogContent>
          </>}
          <DialogActions>
            <Button onClick={() => setDeletePendingDialogOpen(false)}>
              Keep
            </Button>
            <Button
              onClick={() =>
                pendingParticipantToDelete &&
                handleDeletePendingParticipant(pendingParticipantToDelete)
              }
              variant="contained"
              color="error"
            >
              {pendingParticipantToDelete?.cancelOnly ? "Cancel Invitation" : "Remove Participant"}
            </Button>
          </DialogActions>
        </Dialog>

        <ParticipantDetailsDialog
          open={participantDetailsDialogOpen}
          participant={selectedParticipantDetails}
          onClose={() => setParticipantDetailsDialogOpen(false)}
        />

        {/* Notification Popup */}
        {notification && (
          <NotificationPopup
            notification={notification}
            onClose={clearNotification}
          />
        )}
      </Box>
    </Container>
  );
};

export default StudyDashboard;
